import { DateTime } from 'luxon';

const DATE_INVALID = 'Invalid Date';
const PARSERS = ['fromISO', 'fromHTTP', 'fromRFC2822', 'fromSQL'] as const;

type Parsers = (typeof PARSERS)[number];
type Subject = string | number | Date | DateTime | object | null;

const resolvers: Array<(subject: Subject) => DateTime | undefined> = [
  (subject) => {
    if (!subject) throw new Error('Unable to parse the empty value');
    return undefined;
  },
  (subject) => (DateTime.isDateTime(subject) ? subject : undefined),
  (subject) =>
    subject instanceof Date ? DateTime.fromJSDate(subject) : undefined,
  (subject) =>
    typeof subject === 'number'
      ? DateTime.fromMillis(toMilliseconds(subject))
      : undefined,
  (subject) => {
    if (typeof subject === 'string') {
      const date = new Date(subject);

      return date.toString() !== DATE_INVALID
        ? DateTime.fromJSDate(date)
        : undefined;
    }

    return undefined;
  },
  (subject) => {
    let dateTime;

    [...PARSERS].some((parser) => {
      const value = DateTime[parser as Parsers](subject as string);

      if (value.isValid) {
        dateTime = value;
      }

      return value.isValid;
    });

    return dateTime;
  }
];

export function parse(subject: Subject) {
  let dateTime: DateTime | undefined;

  [...resolvers].some((resolve) => {
    const value = resolve(subject);

    if (value) {
      dateTime = value;
    }

    return !!value;
  });

  if (!dateTime || !dateTime.isValid) {
    throw new Error(
      `Unable to parse the value "${subject}" of a type "${typeof subject}"`
    );
  }

  return dateTime;
}

function toMilliseconds(value: number): number {
  const { length } = value.toString();

  // ts 99999999999 (11 length) = 03/03/1973
  return length <= 11 ? value * 1000 : value;
}
